import React, { useState, useEffect, useCallback } from 'react';
import { useFormik, FormikProvider, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import './PurchasesScreen.css';
import appclient from '../../../AppClient';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
// Initial values for a purchase item
const INITIAL_PURCHASE_ITEM = {
  product: '',
  quantity: 1,
  purchase_price: '',
  sale_price: '',
  credit_sale_price: '',
  price_increase: 0,
  price_increase_period: 30,
  total_price: 0
};
const PurchasesScreen = () => {
  const {paymentMethods } = useAuth();
  const navigate = useNavigate();
  // State management
  const [suppliers, setSuppliers] = useState([]);
  const [allPurchases, setAllPurchases] = useState([]);
  const [filteredPurchases, setFilteredPurchases] = useState([]);
  const [products, setProducts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const purchasesPerPage = 10;
  // Improved API calls with proper error handling
  const fetchData = useCallback(async (endpoint, stateSetter, errorMessage) => {
    try {
      setIsLoading(true);
      const response = await appclient.get(endpoint);
      const responseData = Array.isArray(response.data) ? response.data : [];
      stateSetter(responseData);
    } catch (err) {
      console.error(`${errorMessage}:`, err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // More concise API calls using the fetchData function
  const fetchSuppliers = useCallback(() => {
    fetchData('/suppliers/', setSuppliers, 'فشل في تحميل الموردين');
  }, [fetchData]);

  const fetchProducts = useCallback(() => {
    fetchData('/products/', setProducts, 'فشل في تحميل المنتجات');
  }, [fetchData]);

  const fetchStaff = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await appclient.get('/staff/');
      // console.log('Staff API response:', response.data); // Log the response for debugging
      setStaff(response.data.results || response.data.staff || response.data || []);
      setError(null);
    } catch (err) {
      console.error('فشل في تحميل الموظفين:', err);
      if (err.response) {
        setError(err.response.data.detail || 'فشل في تحميل بيانات الموظفين');
      } else {
        setError('فشل في تحميل بيانات الموظفين');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchPurchases = useCallback(() => {
    fetchData('/transactions/purchases/', (data) => {
      setAllPurchases(data);
      setFilteredPurchases(data);
    }, 'فشل في تحميل المشتريات');
  }, [fetchData]);

  // Load data when component mounts
  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
    fetchStaff();
    fetchPurchases();
  }, [fetchSuppliers, fetchProducts, fetchStaff, fetchPurchases]);

  // Handle search with debounce for better performance
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPurchases(allPurchases);
      setCurrentPage(1);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    const filtered = allPurchases.filter(purchase => {
      return (
        (purchase.id?.toString() || '').toLowerCase().includes(searchTermLower) ||
        (purchase.supplier?.name || '').toLowerCase().includes(searchTermLower) ||
        (purchase.payment_method || '').toLowerCase().includes(searchTermLower) ||
        (purchase.total_price?.toString() || '').toLowerCase().includes(searchTermLower)
      );
    });

    setFilteredPurchases(filtered);
    setCurrentPage(1);
  }, [searchTerm, allPurchases]);

  // Memoized calculation functions for better performance
  const calculateLineItemTotal = useCallback((quantity, price) => {
    const qtyValue = parseFloat(quantity) || 0;
    const priceValue = parseFloat(price) || 0;
    return (qtyValue * priceValue).toFixed(2);
  }, []);

  const calculateTotalPrice = useCallback((items, discount = 0) => {
    if (!items || !items.length) return '0.00';

    const total = items.reduce((sum, item) => {
      return sum + (parseFloat(item.total_price) || 0);
    }, 0);

    const discountRate = parseFloat(discount) / 100;
    const discountedTotal = total * (1 - (isNaN(discountRate) ? 0 : discountRate));

    return discountedTotal > 0 ? discountedTotal.toFixed(2) : '0.00';
  }, []);

  // Delete a purchase with improved error handling
  const handleDeletePurchase = useCallback(async (purchaseId) => {
    if (!window.confirm('هل أنت متأكد من رغبتك في الغاء عملية الشراء هذه؟')) {
      return;
    }

    try {
      setIsLoading(true);
      await appclient.delete(`/transactions/purchases/${purchaseId}/cancel/`);
      await fetchPurchases(); // Wait for the fetch to complete
      setError(null); // Clear any previous errors on success
    } catch (err) {
      setError(err.response?.data?.detail || 'فشل في الغاء عملية الشراء');
      console.error('خطأ في اثناء الغاء عملية الشراء:', err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchPurchases]);

  // Validation schema using Yup with more specific validations
  const validationSchema = Yup.object({
    payment_method: Yup.string().required('طريقة الدفع مطلوبة'),
    supplier: Yup.string().required('المورّد مطلوب'),
    discount_value: Yup.number()
      .min(0, 'يجب أن يكون الخصم قيمة موجبة')
      .max(100, 'يجب أن يكون الخصم أقل من أو يساوي 100%')
      .transform((value) => (isNaN(value) ? 0 : value)),
    discount_user_name: Yup.string().when('discount_value', {
      is: (val) => val > 0,
      then: () => Yup.string().required('اسم الموظف المسؤول عن الخصم مطلوب عند تطبيق الخصم'),
      otherwise: () => Yup.string()
    }),
    purchase_items: Yup.array().of(
      Yup.object({
        product: Yup.string().required('المنتج مطلوب'),
        quantity: Yup.number().min(1, 'الكمية يجب أن تكون 1 على الأقل').required('الكمية مطلوبة'),
        purchase_price: Yup.number().min(0, 'السعر يجب أن يكون قيمة موجبة').required('سعر الشراء مطلوب'),
        sale_price: Yup.number().min(0, 'السعر يجب أن يكون قيمة موجبة').required('سعر البيع النقدي مطلوب'),
        credit_sale_price: Yup.number().min(0, 'السعر يجب أن يكون قيمة موجبة').required('سعر البيع بالإئتمان مطلوب'),
        price_increase: Yup.number().min(0, 'الزيادة يجب أن تكون قيمة موجبة'),
        price_increase_period: Yup.number().min(1, 'الفترة يجب أن تكون 1 يوم على الأقل')
      })
    ).min(1, 'يجب إضافة منتج واحد على الأقل')
  });

  // Initialize formik with fixed initial values
  const formik = useFormik({
    initialValues: {
      payment_method: paymentMethods[0]?.name || '',
      number_of_installments: 0,
      supplier: '',
      discount_value: '0',
      discount_user_name: '',
      purchase_items: [{ ...INITIAL_PURCHASE_ITEM }],
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        setError(null);

        // Prepare data for API with safer type conversion
        const purchaseData = {
          ...values,
          supplier: parseInt(values.supplier, 10),
          discount_value: parseFloat(values.discount_value) || 0,
          number_of_installments: parseInt(values.number_of_installments, 10) || 0,
          total_price: parseFloat(calculateTotalPrice(values.purchase_items, values.discount_value)),
          purchase_items: values.purchase_items.map(item => ({
            product: parseInt(item.product, 10),
            quantity: parseInt(item.quantity, 10) || 0,
            purchase_price: parseFloat(item.purchase_price) || 0,
            sale_price: parseFloat(item.sale_price) || 0,
            credit_sale_price: parseFloat(item.credit_sale_price) || 0,
            price_increase: parseFloat(item.price_increase) || 0,
            price_increase_period: parseInt(item.price_increase_period, 10) || 30,
            total_price: parseFloat(item.total_price) || 0
          }))
        };

        await appclient.post('/transactions/purchases/add/', purchaseData);

        // Reset form and refresh data
        formik.resetForm();
        setTotalPrice('0.00');
        await fetchPurchases();
        setError(null); // Clear any previous errors on success
      } catch (err) {
        const errorMsg = err.response?.data?.detail || 'فشل في تسجيل عملية الشراء';
        setError(errorMsg);
        console.error('Error submitting purchase:', err);
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Update item total price when quantity or price changes - optimized
  useEffect(() => {
    if (!formik.values.purchase_items) return;

    // Process in batch
    const updatedItems = formik.values.purchase_items.map(item => ({
      ...item,
      total_price: calculateLineItemTotal(item.quantity, item.purchase_price)
    }));

    // Only update if there are changes
    if (JSON.stringify(updatedItems) !== JSON.stringify(formik.values.purchase_items)) {
      formik.setFieldValue('purchase_items', updatedItems);
    }

    // Update total price with discount applied
    const newTotal = calculateTotalPrice(updatedItems, formik.values.discount_value);
    setTotalPrice(newTotal);
  }, [formik.values.purchase_items, formik.values.discount_value, calculateLineItemTotal, calculateTotalPrice]);

  // Handle product selection with safeguards
  const handleProductSelect = (index, productId) => {
    if (!productId) return;

    const selectedProduct = products.find(p => p.id === parseInt(productId, 10));
    if (!selectedProduct) return;

    // Update fields with default values if needed
    formik.setFieldValue(`purchase_items[${index}].purchase_price`, selectedProduct.purchase_price || '0');
    formik.setFieldValue(`purchase_items[${index}].sale_price`, selectedProduct.sale_price || '0');
    formik.setFieldValue(`purchase_items[${index}].credit_sale_price`, selectedProduct.credit_sale_price || '0');
  };

  // Pagination logic
  const indexOfLastPurchase = currentPage * purchasesPerPage;
  const indexOfFirstPurchase = indexOfLastPurchase - purchasesPerPage;
  const currentPurchases = filteredPurchases.slice(indexOfFirstPurchase, indexOfLastPurchase);
  const totalPages = Math.ceil(filteredPurchases.length / purchasesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Format date helper function
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('ar-EG');
    } catch (e) {
      return 'تاريخ غير صالح';
    }
  };

  // Recent purchases table component - separated for clarity
  const RecentPurchasesTable = () => (
    <>
      <table className="purchases-table">
        <thead>
          <tr>
            <th>الرقم</th>
            <th>المورّد</th>
            <th>طريقة الدفع</th>
            <th>العناصر</th>
            <th>الإجمالي</th>
            <th>التاريخ</th>
            <th>الإجراءات</th>
          </tr>
        </thead>
        <tbody>
          {currentPurchases.map(purchase => (
            <tr key={purchase.id}>
              <td>{purchase.id}</td>
              <td>{purchase.supplier || "مورد غير معروف"}</td>
              <td>{purchase.payment_method}</td>
              <td>{purchase.total_quantity || 0}</td>
              <td>{purchase.total_price || 0}</td>
              <td>{formatDate(purchase.invoice_date)}</td>
              <td>
                <button
                  type="button"
                  className="btn"
                  onClick={() => navigate(`/transactions/purchases/${purchase.id}`)}
                  disabled={isLoading}
                >
                  <i className="fas fa-eye"></i>
                </button>

                {purchase.status === 'completed' ? (
                  <button
                    type="button"
                    className="btn btn-danger delete-btn"
                    onClick={() => handleDeletePurchase(purchase.id)}
                    disabled={isLoading}
                  >
                    <i className="fas fa-trash mx-1"></i>
                    <span>إلغاء العملية</span>
                  </button>
                ) : (
                  <span className="bg-warning text-white px-2 py-1 rounded">ملغاة</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            type="button"
            onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
            disabled={currentPage === 1 || isLoading}
          >
            السابق
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
            <button
              type="button"
              key={number}
              onClick={() => paginate(number)}
              className={currentPage === number ? 'active' : ''}
              disabled={isLoading}
            >
              {number}
            </button>
          ))}

          <button
            type="button"
            onClick={() => paginate(currentPage < totalPages ? currentPage + 1 : totalPages)}
            disabled={currentPage === totalPages || isLoading}
          >
            التالي
          </button>
        </div>
      )}
    </>
  );

  return (
    <div className="purchase-page" dir="rtl" lang="ar">
      <h1>إدارة المشتريات</h1>

      {error && <div className="error-message">{error}</div>}

      <div className="purchase-form-container">
        <h2>إضافة عملية شراء جديدة</h2>
        <FormikProvider value={formik}>
          <Form>
            <div className="form-group">
              <label htmlFor="supplier">المورّد</label>
              <Field
                as="select"
                id="supplier"
                name="supplier"
                className={formik.touched.supplier && formik.errors.supplier ? 'error' : ''}
              >
                <option value="">اختر المورّد</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                ))}
              </Field>

              {formik.touched.supplier && formik.errors.supplier && (
                <div className="error-text">{formik.errors.supplier}</div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="payment_method">طريقة الدفع</label>
                <Field
                  as="select"
                  id="payment_method"
                  name="payment_method"
                  className={formik.touched.payment_method && formik.errors.payment_method ? 'error' : ''}
                >
                  <option value="">اختر طريقة الدفع</option>
                  {paymentMethods.filter(method => method.is_purchase==true).map(method => (
                    <option key={method.name} value={method.name}>{method.arabic_name}</option>
                  ))}
                </Field>
                {formik.touched.payment_method && formik.errors.payment_method && (
                  <div className="error-text">{formik.errors.payment_method}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="number_of_installments">عدد الأقساط</label>
                <Field
                  type="number"
                  id="number_of_installments"
                  name="number_of_installments"
                  min="1"
                  dir="ltr"
                  disabled={formik.values.payment_method !== 'credit'}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="discount_value">قيمة الخصم (%)</label>
                <Field
                  type="number"
                  step="0.01"
                  id="discount_value"
                  name="discount_value"
                  min="0"
                  max="100"
                  dir="ltr"
                />
                {formik.touched.discount_value && formik.errors.discount_value && (
                  <div className="error-text">{formik.errors.discount_value}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="discount_user_name">موظف الخصم</label>
                <Field
                  as="select"
                  id="discount_user_name"
                  name="discount_user_name"
                  disabled={parseFloat(formik.values.discount_value) <= 0}
                  className={formik.touched.discount_user_name && formik.errors.discount_user_name ? 'error' : ''}
                >
                  <option value="">اختر الموظف</option>
                  {Array.isArray(staff) && staff.length > 0 ? (
                    staff.map(employee => (
                      <option key={employee.id} value={employee.name}>
                        {employee.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>لا يوجد موظفين متاحين</option>
                  )}
                </Field>
                {formik.touched.discount_user_name && formik.errors.discount_user_name && (
                  <div className="error-text">{formik.errors.discount_user_name}</div>
                )}
              </div>
            </div>

            <div className="purchase-items">
              <h3>عناصر الشراء</h3>
              <FieldArray name="purchase_items">
                {({ push, remove }) => (
                  <>
                    {formik.values.purchase_items.map((item, index) => (
                      <div key={index} className="purchase-item">
                        <div className="item-header">
                          <h4>الصنف #{index + 1}</h4>
                          {formik.values.purchase_items.length > 1 && (
                            <button
                              type="button"
                              className="remove-item-btn"
                              onClick={() => remove(index)}
                            >
                              إزالة
                            </button>
                          )}
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor={`purchase_items[${index}].product`}>المنتج</label>
                            <div className="product-search-container">
                              <Field
                                as="select"
                                id={`purchase_items[${index}].product`}
                                name={`purchase_items[${index}].product`}
                                onChange={(e) => {
                                  formik.handleChange(e);
                                  handleProductSelect(index, e.target.value);
                                }}
                                className={
                                  formik.touched.purchase_items?.[index]?.product &&
                                    formik.errors.purchase_items?.[index]?.product ? 'error' : ''
                                }
                              >
                                <option value="">اختر المنتج</option>
                                {products.map(product => (
                                  <option key={product.id} value={product.id}>
                                    {product.name} - سعر الشراء: {product.purchase_price} - الكمية: {product.amount}
                                  </option>
                                ))}
                              </Field>
                            </div>
                            {formik.touched.purchase_items?.[index]?.product &&
                              formik.errors.purchase_items?.[index]?.product && (
                                <div className="error-text">{formik.errors.purchase_items[index].product}</div>
                              )}
                          </div>

                          <div className="form-group">
                            <label htmlFor={`purchase_items[${index}].quantity`}>الكمية</label>
                            <Field
                              type="number"
                              id={`purchase_items[${index}].quantity`}
                              name={`purchase_items[${index}].quantity`}
                              min="1"
                              dir="ltr"
                              className={
                                formik.touched.purchase_items?.[index]?.quantity &&
                                  formik.errors.purchase_items?.[index]?.quantity ? 'error' : ''
                              }
                            />
                            {formik.touched.purchase_items?.[index]?.quantity &&
                              formik.errors.purchase_items?.[index]?.quantity && (
                                <div className="error-text">{formik.errors.purchase_items[index].quantity}</div>
                              )}
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor={`purchase_items[${index}].purchase_price`}>سعر الشراء</label>
                            <Field
                              type="number"
                              step="0.01"
                              id={`purchase_items[${index}].purchase_price`}
                              name={`purchase_items[${index}].purchase_price`}
                              dir="ltr"
                              className={
                                formik.touched.purchase_items?.[index]?.purchase_price &&
                                  formik.errors.purchase_items?.[index]?.purchase_price ? 'error' : ''
                              }
                            />
                            {formik.touched.purchase_items?.[index]?.purchase_price &&
                              formik.errors.purchase_items?.[index]?.purchase_price && (
                                <div className="error-text">{formik.errors.purchase_items[index].purchase_price}</div>
                              )}
                          </div>

                          <div className="form-group">
                            <label htmlFor={`purchase_items[${index}].sale_price`}>سعر البيع النقدي</label>
                            <Field
                              type="number"
                              step="0.01"
                              id={`purchase_items[${index}].sale_price`}
                              name={`purchase_items[${index}].sale_price`}
                              dir="ltr"
                              className={
                                formik.touched.purchase_items?.[index]?.sale_price &&
                                  formik.errors.purchase_items?.[index]?.sale_price ? 'error' : ''
                              }
                            />
                            {formik.touched.purchase_items?.[index]?.sale_price &&
                              formik.errors.purchase_items?.[index]?.sale_price && (
                                <div className="error-text">{formik.errors.purchase_items[index].sale_price}</div>
                              )}
                          </div>

                          <div className="form-group">
                            <label htmlFor={`purchase_items[${index}].credit_sale_price`}>سعر البيع بالإئتمان</label>
                            <Field
                              type="number"
                              step="0.01"
                              id={`purchase_items[${index}].credit_sale_price`}
                              name={`purchase_items[${index}].credit_sale_price`}
                              dir="ltr"
                              className={
                                formik.touched.purchase_items?.[index]?.credit_sale_price &&
                                  formik.errors.purchase_items?.[index]?.credit_sale_price ? 'error' : ''
                              }
                            />
                            {formik.touched.purchase_items?.[index]?.credit_sale_price &&
                              formik.errors.purchase_items?.[index]?.credit_sale_price && (
                                <div className="error-text">{formik.errors.purchase_items[index].credit_sale_price}</div>
                              )}
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor={`purchase_items[${index}].price_increase`}>زيادة السعر</label>
                            <Field
                              type="number"
                              step="0.01"
                              id={`purchase_items[${index}].price_increase`}
                              name={`purchase_items[${index}].price_increase`}
                              dir="ltr"
                              className={
                                formik.touched.purchase_items?.[index]?.price_increase &&
                                  formik.errors.purchase_items?.[index]?.price_increase ? 'error' : ''
                              }
                            />
                            {formik.touched.purchase_items?.[index]?.price_increase &&
                              formik.errors.purchase_items?.[index]?.price_increase && (
                                <div className="error-text">{formik.errors.purchase_items[index].price_increase}</div>
                              )}
                          </div>

                          <div className="form-group">
                            <label htmlFor={`purchase_items[${index}].price_increase_period`}>فترة الزيادة (أيام)</label>
                            <Field
                              type="number"
                              id={`purchase_items[${index}].price_increase_period`}
                              name={`purchase_items[${index}].price_increase_period`}
                              min="1"
                              dir="ltr"
                              className={
                                formik.touched.purchase_items?.[index]?.price_increase_period &&
                                  formik.errors.purchase_items?.[index]?.price_increase_period ? 'error' : ''
                              }
                            />
                            {formik.touched.purchase_items?.[index]?.price_increase_period &&
                              formik.errors.purchase_items?.[index]?.price_increase_period && (
                                <div className="error-text">{formik.errors.purchase_items[index].price_increase_period}</div>
                              )}
                          </div>

                          <div className="form-group">
                            <label>إجمالي الصنف</label>
                            <input
                              type="text"
                              value={formik.values.purchase_items[index].total_price || '0.00'}
                              readOnly
                              dir="ltr"
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      className="add-item-btn"
                      onClick={() => push({ ...INITIAL_PURCHASE_ITEM })}
                      disabled={isLoading}
                    >
                      إضافة صنف آخر
                    </button>
                  </>
                )}
              </FieldArray>
            </div>

            <div className="total-section">
              <h3>الإجمالي بعد الخصم: {totalPrice}</h3>
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn" disabled={isLoading || !formik.isValid}>
                {isLoading ? 'جاري الإرسال...' : 'تسجيل عملية الشراء'}
              </button>
            </div>
          </Form>
        </FormikProvider>
      </div>

      <div className="purchases-list">
        <div className="purchases-header">
          <h2>عمليات الشراء الأخيرة</h2>
          <div className="search-bar">
            <input
              type="text"
              placeholder="ابحث عن مشتريات..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={isLoading}
            />
            <i className="fas fa-search"></i>
          </div>
        </div>

        {isLoading ? (
          <p>جاري تحميل المشتريات...</p>
        ) : filteredPurchases.length === 0 ? (
          <p>لا توجد عمليات شراء</p>
        ) : (
          <RecentPurchasesTable />
        )}
      </div>
    </div>
  );
};

export default PurchasesScreen;